import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { pickImageStyle } from '@/utils/transforms';
import parse from 'html-react-parser';
import { useRouter } from 'next/router';
import Hidden from '@mui/material/Hidden';
import Image from 'next/image';

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      position: 'relative',
      textAlign: 'center',

      '&.bgOverlay': {
        '&:after': {
          pointerEvents: 'none',
          content: "''",
          width: '100%',
          height: '100%',
          bottom: 0,
          top: 0,
          left: 0,
          position: 'absolute',
        },
      },
      '&.bgOverlayVideo': {
        [theme.breakpoints.up('md')]: {
          overflow: 'hidden',
        },
      },
      '&.bgOverlayVideo:after': {
        maxHeight: '760px',
        height: '54.25vw !important',
        [theme.breakpoints.down('xl')]: {
          height: 'calc(54.25vw - .5rem) !important',
        },
        [theme.breakpoints.down('xl')]: {
          height: 'calc(54.25vw - .5rem) !important',
        },
      },
      '&.noOverlay': {
        '&:after': {
          pointerEvents: 'none',
          content: "''",
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent',
          top: 0,
          left: 0,
          position: 'absolute',
        },
        '&:hover': {
          opacity: 1,
        },
      },
      '& .title, & .subtitle': {
        margin: 0,
        color: '#fff',
        fontWeight: 700
      },

      '& .banner-sm, & .banner-sm .imgWrapper': {
        minHeight: 350,
        '& .subtitle': {
          fontSize: 24,
          [theme.breakpoints.down('md')]: { fontSize: 20 },
        },
        '& .title': {
          fontSize: 60,
          fontWeight: 700,
          [theme.breakpoints.down('md')]: { fontSize: 36 },
        },
      },

      '& .banner-md, & .banner-lg, & .banner-video': {
        '& .subtitle': {
          fontSize: 30,
          [theme.breakpoints.down('xl')]: {
            fontSize: 26,
          },
          [theme.breakpoints.down('md')]: {
            fontSize: 22,
            margin: '.5rem 0',
          },
        },
        '& .title': {
          fontSize: 80,
          fontWeight: 700,
          [theme.breakpoints.down('xl')]: {
            fontSize: 60,
          },
          [theme.breakpoints.down('md')]: {
            fontSize: 32,
          },
        },
      },
      '& .banner-md, & .banner-md .imgWrapper': {
        minHeight: 800,
      },
      '& .banner-lg, & .banner-lg .imgWrapper': {
        minHeight: 855,
      },
      '& .banner-video': {
        // display: 'none',
        [theme.breakpoints.up('md')]: {
          display: 'block',
          position: 'relative',
          height: '52.75vw',
          maxHeight: '760px',
          // minHeight: '760px',
        },
      },
      '& .banner-xl, & .banner-xl .imgWrapper': {
        minHeight: 760,
        [theme.breakpoints.down('xl')]: { minHeight: 620 },
        [theme.breakpoints.down('md')]: { minHeight: 480 },
        '& .subtitle': {
          fontSize: 24,
          [theme.breakpoints.down('xl')]: {
            fontSize: 26,
          },
          [theme.breakpoints.down('md')]: {
            fontSize: 24,
          },
        },
        '& .title': {
          fontSize: 80,
          fontWeight: 700,
          [theme.breakpoints.down('xl')]: {
            fontSize: 60,
          },
          [theme.breakpoints.down('md')]: {
            fontSize: 28,
          },
        },
      },
      '& h3': {
        textTransform: 'none',
      },
    },
    communityImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
      marginBottom: '16px',
      position: 'absolute',
      top: 0,
      left: 0,
    },

    communityVideo: {
      width: '100%',
      height: 'auto',
      [theme.breakpoints.up('md')]: {
        height: 'auto',
      },
      position: 'absolute',
      marginBottom: '16px',
      top: 0,
      left: 0,
    },
    coverLink: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 20,
    },
    locationInfo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    bannerContent: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      left: 0,
      width: 'calc(100%)',
      height: 'auto',
      color: 'white',
      zIndex: 10,
      padding: '0 2rem',
      display: 'flex',
      flexDirection: 'column',
    },
    communityTitle: {
      display: 'block',
      borderBottom: 'solid thin white',
      textAlign: 'center',
      fontSize: 18,
    },
    locationDescription: {
      fontSize: 12,
      lineHeight: '14.4px',
    },
    afterBorder: {
      '& span': {
        position: 'relative',
      },
      '& span:after': {
        content: '""',
        width: '90%',
        height: '1px',
        margin: '0 auto',
        bottom: '-1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        position: 'absolute',
      },
    },
    readMoreLink: {
      textDecoration: 'underline',
      color: theme.palette.grey[900],
    },
  })
);

const Banner = (props) => {
  const router = useRouter();
  let { node, block } = props;
  node = !node && block ? block : node;

  let {
    imgAlt,
    imgTitle,
    imgWidth,
    imgHeight,
    imageOrig,
    image_styles,
    video,
    videoHosted,
    videoHostedEnd,
    subtitle,
    ctaLinks,
    bannerSize,
    title,
  } = node;

  bannerSize =
    !bannerSize && video?.videoEmbed ? 'video' : bannerSize ? bannerSize : 'md';
  let videoEmbed = null;
  if (video?.videoEmbed) {
    videoEmbed = video.videoEmbed;
    if (video.videoEmbed.includes('https://youtu.be')) {
      videoEmbed = video.videoEmbed.replace(
        'https://youtu.be',
        'https://youtube.com/embed'
      );
    }
  }
  const classes = useStyles(props);

  let getImageStyle = pickImageStyle(
    {
      imgAlt,
      imgTitle,
      imgWidth,
      imgHeight,
      imageOrig,
      image_styles,
      title,
    },
    'max_width_1400',
    `/images/placeholder/clubhouse.jpg`
  );
  ({ imgAlt, imgTitle, imgWidth, imgHeight, imageOrig } = getImageStyle);
  function handleVideoEnded(e) {
    const introVideo = document.getElementById('introVideo');
    const endingVideo = document.getElementById('endingVideo');
    introVideo.classList.add('hidden');
    endingVideo.play();
  }
  return (
    <Box
      displayPrint="none"
      px={!videoEmbed ? 0 : 0}
      className={
        (title !== null && !videoEmbed ? ' bgOverlay ' : ' noOverlay ') +
        (videoHosted ? 'bgOverlayVideo ' : ' ') +
        classes.root +
        (router?.asPath.includes('/about') ? ' about-banner' : '') +
        (bannerSize === 'md' ? ' large-banner' : '')
      }
    >
      <Box className="ctaWrapper">
        <Box className={`banner banner-${bannerSize}`}>
          {imageOrig && !videoEmbed && !videoHosted && (
            <div className={'imgWrapper'} style={{position: 'relative', height: '100%'}}>
              <Image
                // height={imgHeight}
                // width={imgWidth}
                className={classes.communityImage}
                src={imageOrig}
                alt={imgAlt || imgTitle || ''}
                title={imgTitle}
                fill={true}
                priority
              />
            </div>
          )}
          {videoEmbed && (
            <iframe
              frameBorder="0"
              allow="autoplay"
              height="100%"
              width="100%"
              className={`embed-banner ${classes.communityEmbedVideo}`}
              htmlallow="autoplay; fullscreen"
              allowFullScreen={true}
              src={`${videoEmbed}`} // ?autoplay=1&loop=1&autopause=0&mute=1&title=0&byline=0&portrait=0
              title="Video playback"
            />
          )}
          {videoHosted && videoHostedEnd && (
            <div>
              <video
                onEnded={handleVideoEnded}
                id="introVideo"
                className={`video-banner ${classes.communityVideo}`}
                autoPlay={true}
                muted={true}
              >
                <source src={videoHosted} type="video/mp4" />
              </video>
              <video
                id="endingVideo"
                className={`video-banner ${classes.communityVideo}`}
                autoPlay={true}
                muted={true}
                loop={true}
              >
                <source src={videoHostedEnd} type="video/mp4" />
              </video>
            </div>
          )}
          {videoHosted && !videoHostedEnd && (
            <Hidden mdDown>
              <video
                className={`video-banner ${classes.communityVideo}`}
                autoPlay={true}
                muted={true}
                loop={true}
              >
                <source src={videoHosted} type="video/mp4" />
              </video>
            </Hidden>
          )}
          <Box className={`${classes.bannerContent} ctaContent`}>
            {!videoEmbed && !videoHosted && (
              <>
                <Typography
                  key={`subtitle`}
                  align="center"
                  className="subtitle"
                  component="p"
                  variant="h3"
                >
                  {subtitle}
                </Typography>
                <Typography
                  key={`title`}
                  align="center"
                  className="title"
                  component="p"
                  variant="h3"
                >
                  {title}
                </Typography>
                <Box mt={4} className="bannerButtonWrapper">
                  <Grid container justifyContent="center" spacing={4}>
                    {ctaLinks?.map((bannerButton, index) => (
                      <Grid item key={`banner-links-${index}`}>
                        <Button
                          disableElevation
                          variant="contained"
                          color="primary"
                          href={bannerButton.second}
                        >
                          {parse(bannerButton.first)}
                        </Button>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default Banner;
